import type { LineSeries } from "@mui/x-charts-pro"
import type { HawaiiCovid } from "@phc/common/gen/threatAnalyticsGen"
import { useMemo } from "react"
import { dayjsUTC } from "../../../../utils/dates"
import {
  CLINICAL_SHAPES,
  type BaseLegendItemData,
} from "../../../Charts/legendUtils"
import {
  DEFAULT_SERIES_COLORS,
  type DatasetEntry,
} from "../../timeSeriesConstants"
import { fillWeeklyGaps } from "../../timeSeriesUtils"
import {
  isHawaiiCovidKey,
  type HawaiiCovidChartData,
} from "./HawaiiCovidTimeSeries"

const EMPTY_CHART_DATA: HawaiiCovidChartData = {
  dataset: [],
  dates: [],
  casesChart: {
    series: [],
    legendItems: [],
  },
  positivityChart: {
    series: [],
    legendItems: [],
  },
}

interface Target {
  id: keyof HawaiiCovid
  label: string
  toPercent?: boolean
}

export function useHawaiiCovidDataTransform(
  hawaiiCovidData: HawaiiCovid[] | undefined
): HawaiiCovidChartData {
  return useMemo(() => {
    if (!hawaiiCovidData || hawaiiCovidData.length === 0) {
      return EMPTY_CHART_DATA
    }

    const targets: Target[] = [
      { id: "weekly_cases", label: "Weekly Cases" },
      { id: "rolling_avg_cases_last_7_days", label: "7-Day Rolling Avg Cases" },
      {
        id: "weekly_test_positivity",
        label: "Weekly Test Positivity %",
        toPercent: true,
      },
      {
        id: "rolling_avg_test_positivity_last_7_days",
        label: "7-Day Rolling Avg Test Positivity %",
        toPercent: true,
      },
    ]

    const allSeries = targets.map(
      (target, i) =>
        ({
          id: target.id,
          dataKey: target.id,
          label: target.label,
          shape: CLINICAL_SHAPES[i % CLINICAL_SHAPES.length] ?? "circle",
          color: DEFAULT_SERIES_COLORS[i % DEFAULT_SERIES_COLORS.length] ?? "",
          showMark: true,
          curve: "linear",
          connectNulls: true,
          valueFormatter: (value: number | null) => {
            if (value === null) return "N/A"
            const isPercent = "toPercent" in target
            return isPercent ? `${value.toFixed(2)}%` : String(value)
          },
        }) satisfies LineSeries
    )

    const casesSeries = allSeries.slice(0, 2)
    const positivitySeries = allSeries.slice(2, 4)
    const casesLegendItems: BaseLegendItemData[] = casesSeries.map(s => ({
      id: s.id,
      label: s.label,
      shape: s.shape,
      color: s.color,
    }))
    const positivityLegendItems: BaseLegendItemData[] = positivitySeries.map(
      s => ({
        id: s.id,
        label: s.label,
        shape: s.shape,
        color: s.color,
      })
    )

    const weeklyTargets: Target[] = [
      { id: "weekly_cases", label: "Weekly Cases" },
      {
        id: "weekly_test_positivity",
        label: "Weekly Test Positivity %",
        toPercent: true,
      },
    ]

    const rollingAvgTargets: Target[] = [
      { id: "rolling_avg_cases_last_7_days", label: "7-Day Rolling Avg Cases" },
      {
        id: "rolling_avg_test_positivity_last_7_days",
        label: "7-Day Rolling Avg Test Positivity %",
        toPercent: true,
      },
    ]

    const { filteredDates: weeklyDates, filteredDataByWeek } =
      getWeeklyNumbers(hawaiiCovidData)
    const { filteredDates: dailyDates, filteredDataByDay } =
      getRollingAvgPerDay(hawaiiCovidData)

    if (
      (weeklyDates.length === 0 || filteredDataByWeek.size === 0) &&
      (dailyDates.length === 0 || filteredDataByDay.size === 0)
    ) {
      return EMPTY_CHART_DATA
    }

    const { dataset: filledDataset, dates: denseDates } =
      mergeWeeklyAndDailyData({
        weeklyDates,
        filteredDataByWeek,
        weeklyTargets,
        dailyDates,
        filteredDataByDay,
        rollingAvgTargets,
      })

    return {
      dataset: filledDataset,
      dates: denseDates,
      casesChart: { series: casesSeries, legendItems: casesLegendItems },
      positivityChart: {
        series: positivitySeries,
        legendItems: positivityLegendItems,
      },
    }
  }, [hawaiiCovidData])
}

function computeDataset(
  sortedDateStrs: string[],
  weeklyDataset: DatasetEntry[],
  filteredDataByDay: Map<string, HawaiiCovid>,
  weeklyTargets: Target[],
  rollingAvgTargets: Target[]
): { dataset: DatasetEntry[]; dates: Date[] } {
  const dataset: DatasetEntry[] = []
  const dates: Date[] = []

  for (const dateStr of sortedDateStrs) {
    const dateObj = dayjsUTC(dateStr).toDate()
    dates.push(dateObj)

    const weeklyEntry = weeklyDataset.find(
      e => dayjsUTC(e.date).format("YYYY-MM-DD") === dateStr
    )
    const dailySample = filteredDataByDay.get(dateStr)

    const entry: DatasetEntry = { date: dateObj }

    weeklyTargets.forEach(t => {
      const val = weeklyEntry?.[t.id]
      entry[t.id] = val != null && typeof val === "number" ? val : null
    })

    rollingAvgTargets.forEach(t => {
      if (dailySample) {
        const rawVal = isHawaiiCovidKey(t.id) ? dailySample[t.id] : undefined
        if (rawVal != null && typeof rawVal === "number") {
          entry[t.id] = "toPercent" in t ? rawVal * 100 : rawVal
        } else {
          entry[t.id] = null
        }
      } else {
        entry[t.id] = null
      }
    })

    dataset.push(entry)
  }

  return { dataset, dates }
}

/**
 * Merges weekly data (with gap filling) and daily rolling avg data into a unified dataset.
 * Weekly: one point per week. Daily: one point per day (no aggregation).
 */
function mergeWeeklyAndDailyData({
  weeklyDates,
  filteredDataByWeek,
  weeklyTargets,
  dailyDates,
  filteredDataByDay,
  rollingAvgTargets,
}: {
  weeklyDates: string[]
  filteredDataByWeek: Map<string, HawaiiCovid>
  weeklyTargets: Target[]
  dailyDates: string[]
  filteredDataByDay: Map<string, HawaiiCovid>
  rollingAvgTargets: Target[]
}): { dataset: DatasetEntry[]; dates: Date[] } {
  const { dataset: weeklyDataset, dates: weeklyDenseDates } = fillWeeklyGaps(
    weeklyDates,
    filteredDataByWeek,
    (dateObj, sample) => {
      const entry: DatasetEntry = { date: dateObj }
      weeklyTargets.forEach(t => {
        if (!sample) {
          entry[t.id] = null
        } else {
          const rawVal = isHawaiiCovidKey(t.id) ? sample[t.id] : undefined
          if (rawVal != null && typeof rawVal === "number") {
            entry[t.id] = "toPercent" in t ? rawVal * 100 : rawVal
          } else {
            entry[t.id] = null
          }
        }
      })
      rollingAvgTargets.forEach(t => {
        entry[t.id] = null
      })
      return entry
    },
    dateObj => {
      return [...weeklyTargets, ...rollingAvgTargets].reduce(
        (acc, t) => ({ ...acc, [t.id]: null }),
        { date: dateObj }
      ) satisfies DatasetEntry
    }
  )

  const weeklyDateSet = new Set(
    weeklyDenseDates.map(d => dayjsUTC(d).format("YYYY-MM-DD"))
  )
  const allDateStrs = new Set<string>([...weeklyDateSet, ...dailyDates])
  const sortedDateStrs = Array.from(allDateStrs).sort()

  const { dataset, dates } = computeDataset(
    sortedDateStrs,
    weeklyDataset,
    filteredDataByDay,
    weeklyTargets,
    rollingAvgTargets
  )

  return { dataset, dates }
}

/**
 * Gets weekly numbers (weekly_cases, weekly_test_positivity) - one value per week.
 * Data is aggregated by week_ended_at.
 */
function getWeeklyNumbers(hawaiiCovidData: HawaiiCovid[]): {
  filteredDates: string[]
  filteredDataByWeek: Map<string, HawaiiCovid>
} {
  const dataByWeek = new Map<string, HawaiiCovid>()
  hawaiiCovidData.forEach(d => {
    const existing = dataByWeek.get(d.week_ended_at)
    if (!existing || (d.reported_at && d.reported_at > existing.reported_at)) {
      dataByWeek.set(d.week_ended_at, d)
    }
  })

  const sortedEndDates = Array.from(dataByWeek.keys()).sort()

  if (sortedEndDates.length === 0) {
    return { filteredDates: [], filteredDataByWeek: new Map() }
  }

  const filteredDataByWeek = new Map<string, HawaiiCovid>()
  for (const d of sortedEndDates) {
    const sample = dataByWeek.get(d)
    if (sample) filteredDataByWeek.set(d, sample)
  }

  return { filteredDates: sortedEndDates, filteredDataByWeek }
}

/**
 * Gets rolling average numbers per day (rolling_avg_cases_last_7_days, rolling_avg_test_positivity_last_7_days).
 * One value per day - uses reported_at as the date, no aggregation over the week.
 */
function getRollingAvgPerDay(hawaiiCovidData: HawaiiCovid[]): {
  filteredDates: string[]
  filteredDataByDay: Map<string, HawaiiCovid>
} {
  const dataByDay = new Map<string, HawaiiCovid>()
  hawaiiCovidData.forEach(d => {
    const dateKey = d.reported_at
      ? dayjsUTC(d.reported_at).format("YYYY-MM-DD")
      : d.week_ended_at
    const existing = dataByDay.get(dateKey)
    if (!existing || (d.reported_at && d.reported_at > existing.reported_at)) {
      dataByDay.set(dateKey, d)
    }
  })

  const sortedDates = Array.from(dataByDay.keys()).sort()

  const firstDate = sortedDates[0]
  if (!firstDate) {
    return { filteredDates: [], filteredDataByDay: new Map() }
  }

  const maxDate = sortedDates.reduce((max, d) => {
    const date = dayjsUTC(d)
    return date.isAfter(max) ? date : max
  }, dayjsUTC(firstDate))

  const sixMonthsAgo = maxDate.subtract(6, "month")
  const filteredDates = sortedDates.filter(
    d => dayjsUTC(d).isAfter(sixMonthsAgo) || dayjsUTC(d).isSame(sixMonthsAgo)
  )

  if (filteredDates.length === 0) {
    return { filteredDates: [], filteredDataByDay: new Map() }
  }

  const filteredDataByDay = new Map<string, HawaiiCovid>()
  for (const d of filteredDates) {
    const sample = dataByDay.get(d)
    if (sample) filteredDataByDay.set(d, sample)
  }

  return { filteredDates, filteredDataByDay }
}
